import { useCallback, useRef, useState } from 'react';

import { DROPDOWN_TRANSITION_DURATION } from '@adflow/frontend/ui-common';

/**
 * This delay is the animation time set for the dropdown to close
 * plus 100ms to give account for react timing delays.
 */
const INPUT_HIDE_DELAY = DROPDOWN_TRANSITION_DURATION.exit + 100;

interface UseInlineSelectorOptions {
	onClose: () => void | Promise<void>;
}

export function useInlineSelector({ onClose }: UseInlineSelectorOptions) {
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [showInput, setShowInput] = useState(false);
	const [showDropdown, setShowDropdown] = useState<boolean>(false);

	const onClickAway = useCallback(() => {
		if (showInput) {
			/**
			 * When closing the dropdown, we first hide the dropdown
			 * then the entire selector after a delay. This gives the
			 * dropdown time to finish its closing animation. The
			 * selector *must* be visible during the closing animation
			 * so that the dropdown can position itself properly against
			 * the input.
			 */
			setShowDropdown(false);
			setTimeout(() => {
				setShowInput(false);
			}, INPUT_HIDE_DELAY);

			void onClose();
		}
	}, [onClose, showInput]);

	const onOpen = useCallback(() => {
		setShowInput(true);

		/**
		 * To reduce clicks and give the feeling that the user is
		 * interacting with the same element, we immediately
		 * show the dropdown and focus the input.
		 */
		setShowDropdown(true);
		setTimeout(() => {
			/**
			 * We focus the input after a short delay because
			 * the input may still be hidden if we focus it immediately.
			 */
			inputRef.current?.focus();
		}, 50);
	}, []);

	return {
		inputRef,
		showInput,
		showDropdown,
		onClickAway,
		onOpen,
	};
}
